import { Component, Inject, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getStorage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';

export interface TimeSheetTemplateData {
    companyName: string;
    companyDetails: string;
    phoneNumber: string;
    header: string;
    terms: string;
    footer: string;
    logoUrl?: string;
    logoBase64?: string; // Added
    signatureUrl?: string;
    signatureBase64?: string; // Added
}

@Component({
    selector: 'app-time-sheet-template-dialog',
    templateUrl: './time-sheet-template-dialog.component.html',
    styleUrls: ['./time-sheet-template-dialog.component.scss']
})
export class TimeSheetTemplateDialogComponent implements OnInit, AfterViewInit {

    @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
    private ctx!: CanvasRenderingContext2D;
    private isDrawing = false;

    today = new Date();
    selectedTabIndex = 0;

    constructor(
        public dialogRef: MatDialogRef<TimeSheetTemplateDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: TimeSheetTemplateData,
        private ux: UxserviceService
    ) { }

    ngOnInit(): void {
        // Ensure all fields are initialized to avoid null issues
        this.data.companyName = this.data.companyName || '';
        this.data.companyDetails = this.data.companyDetails || '';
        this.data.phoneNumber = this.data.phoneNumber || '';
        this.data.header = this.data.header || '';
        this.data.terms = this.data.terms || '';
        this.data.footer = this.data.footer || '';
    }

    ngAfterViewInit() {
        // We might need to handle tab change to init canvas if it's inside a tab that is not initially visible
    }

    save() {
        this.dialogRef.close(this.data);
    }

    close() {
        this.dialogRef.close();
    }

    // ==========================================
    // LOGO UPLOAD
    // ==========================================
    triggerLogoInput() {
        document.getElementById('tsLogoInput')?.click();
    }

    async onLogoSelected(event: any) {
        const file = event.target.files[0];
        if (file) {
            try {
                this.ux.showOverlay('Uploading Logo...');
                
                // Convert to Base64 for instant PDF use
                const base64 = await this.fileToBase64(file);

                // Upload to Firebase Storage
                const storage = getStorage();
                const path = `company_logos/ts_upload_${Date.now()}_${file.name}`;
                const storageRef = ref(storage, path);

                const uploadResult = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(uploadResult.ref);

                this.data.logoUrl = url;
                this.data.logoBase64 = base64; // Added base64
                this.ux.showSuccess('Logo uploaded!');
            } catch (error) {
                console.error('Error uploading logo:', error);
                this.ux.showError('Error uploading logo');
            } finally {
                this.ux.instantCloseOverlay();
            }
        }
    }

    // ==========================================
    // SIGNATURE: UPLOAD IMAGE
    // ==========================================
    triggerSigInput() {
        document.getElementById('sigInputTs')?.click();
    }

    async onSigSelected(event: any) {
        const file = event.target.files[0];
        if (file) {
            try {
                this.ux.showOverlay('Uploading Signature...');
                
                // Convert to Base64 for instant PDF use
                const base64 = await this.fileToBase64(file);

                // Upload to Firebase Storage
                const storage = getStorage();
                const path = `signatures/${Date.now()}_${file.name}`;
                const storageRef = ref(storage, path);

                const uploadResult = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(uploadResult.ref);

                this.data.signatureUrl = url;
                this.data.signatureBase64 = base64; // Added base64
                this.ux.showSuccess('Signature uploaded!');
            } catch (error) {
                console.error('Error uploading signature:', error);
                this.ux.showError('Error uploading signature');
            } finally {
                this.ux.instantCloseOverlay();
            }
        }
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    }

    // ==========================================
    // SIGNATURE: DRAW CANVAS
    // ==========================================

    initCanvas() {
        if (this.sigCanvas) {
            const canvas = this.sigCanvas.nativeElement;
            this.ctx = canvas.getContext('2d')!;
            this.ctx.lineWidth = 2;
            this.ctx.lineCap = 'round';
            this.ctx.strokeStyle = '#000000';
        }
    }

    startDrawing(event: MouseEvent) {
        if (!this.ctx) this.initCanvas();
        this.isDrawing = true;
        this.ctx.beginPath();
        this.ctx.moveTo(event.offsetX, event.offsetY);
    }

    draw(event: MouseEvent) {
        if (!this.isDrawing) return;
        this.ctx.lineTo(event.offsetX, event.offsetY);
        this.ctx.stroke();
    }

    stopDrawing() {
        this.isDrawing = false;
        this.ctx.closePath();
    }

    clearCanvas() {
        if (this.sigCanvas) {
            const canvas = this.sigCanvas.nativeElement;
            this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    async saveCanvasSignature() {
        if (!this.sigCanvas) return;
        const canvas = this.sigCanvas.nativeElement;

        this.ux.showOverlay('Saving Signature...');
        
        // Get base64 directly from canvas
        const base64 = canvas.toDataURL('image/png');

        canvas.toBlob(async (blob) => {
            if (blob) {
                try {
                    const storage = getStorage();
                    const path = `signatures/drawn_${Date.now()}.png`;
                    const storageRef = ref(storage, path);

                    const uploadResult = await uploadBytes(storageRef, blob);
                    const url = await getDownloadURL(uploadResult.ref);

                    this.data.signatureUrl = url;
                    this.data.signatureBase64 = base64; // Added base64
                    this.ux.showSuccess('Signature saved!');
                } catch (error) {
                    console.error('Error saving drawn signature:', error);
                    this.ux.showError('Error saving signature');
                } finally {
                    this.ux.instantCloseOverlay();
                }
            }
        }, 'image/png');
    }

}

